import { supabase } from './supabase';
import { env } from './env';
import { ApiCallError, type ApiResponse } from './apiErrors';

export type { ApiError, ApiFailure, ApiResponse, ApiSuccess } from './apiErrors';
export { ApiCallError } from './apiErrors';

/**
 * Invoke a Clinio Edge Function and unwrap its `{ ok, data | error }` envelope.
 * Throws ApiCallError for any non-ok response so call sites can use try/catch.
 */
export async function callFunction<TInput extends Record<string, unknown>, TOutput>(
  name: string,
  body: TInput,
): Promise<TOutput> {
  const { data: sessionData } = await supabase.auth.getSession();
  const sessionToken = sessionData?.session?.access_token;
  // Logged-out pages (forgot password, invite accept, clinic signup) have no
  // user JWT. supabase.functions.invoke used the anon key as Bearer then.
  // A session still sends that user's access token. apikey stays the anon key
  // in both cases, which is what the hosted gateway expects.
  const token = sessionToken || env.VITE_SUPABASE_ANON_KEY;

  // Raw fetch with an explicit JSON string. supabase.functions.invoke has
  // dropped the body on some Safari POSTs (empty application/json → gateway
  // 400 without CORS, shown as "Failed to send a request to the Edge Function").
  let resp: Response;
  try {
    resp = await fetch(`${env.VITE_SUPABASE_URL}/functions/v1/${name}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        apikey: env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'x-application-name': 'clinio-web',
      },
      body: JSON.stringify(body ?? {}),
    });
  } catch {
    throw new ApiCallError({
      code: 'internal-error',
      message: 'Failed to send a request to the Edge Function',
    });
  }

  const json = (await resp.json().catch(() => null)) as ApiResponse<TOutput> | null;
  if (json && json.ok === false) {
    throw new ApiCallError(json.error);
  }
  if (json && json.ok === true) {
    return json.data;
  }
  throw new ApiCallError({
    code: resp.status === 401 ? 'unauthorized' : 'internal-error',
    message: `Function call failed (${resp.status})`,
  });
}
